import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, Building2, FileText, Target, Trash2, UserRound } from 'lucide-react';
import { useDeleteDatasetItem, useEvalDatasetDetail } from '@/hooks/useEvaluator';
import { isStructuredPersona, type LangfuseDatasetItem } from '@/types/evaluator';
import { Skeleton } from '@/components/ui/skeleton';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface DatasetItemsListProps {
  orgId: string | undefined;
  datasetName: string;
  onSelectItem: (itemId: string) => void;
}

export function DatasetItemsList({ orgId, datasetName, onSelectItem }: DatasetItemsListProps) {
  const { data, isLoading, error } = useEvalDatasetDetail(datasetName, orgId);
  const [sortNewest, setSortNewest] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<LangfuseDatasetItem | null>(null);
  const deleteMutation = useDeleteDatasetItem();

  const items = data?.items ?? [];

  const sorted = [...items].sort((a, b) => {
    const aDate = a.created_at ?? '';
    const bDate = b.created_at ?? '';
    return sortNewest ? bDate.localeCompare(aDate) : aDate.localeCompare(bDate);
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-[64px] items-center justify-between gap-4 border-b px-5 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="inline-flex items-center truncate text-sm font-medium text-foreground">
            Personas
            <HelpTooltip text="The simulated people in this population." />
          </h2>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="rounded-md bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
            {items.length} {items.length === 1 ? 'persona' : 'personas'}
          </span>
          {sorted.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={() => setSortNewest((prev) => !prev)}
            >
              <ArrowUpDown className="mr-1 h-3 w-3" />
              {sortNewest ? 'Newest first' : 'Oldest first'}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map((item) => (
              <Skeleton key={item} className="h-24 rounded-md" />
            ))}
          </div>
        )}
        {error && <p className="px-5 py-4 text-sm text-destructive">Failed to load personas.</p>}
        {!isLoading && !error && sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm font-medium text-heading">No personas in this population yet.</p>
          </div>
        )}
        <div className="divide-y">
          {sorted.map((item: LangfuseDatasetItem) => {
            const input = item.input;
            const structured = isStructuredPersona(input);

            let title: string;
            let subtitle: string;
            let meta: string;
            let goal: string;
            if (structured) {
              const employer = input.company
                ? (input.industry ? `${input.company} — ${input.industry}` : input.company)
                : '';
              title = input.name;
              subtitle = input.title;
              meta = employer;
              goal = input.goal ?? '';
            } else {
              const legacyInput = (input ?? {}) as Record<string, unknown>;
              const legacyPersona = typeof legacyInput.persona === 'string' ? legacyInput.persona : '';
              const legacyGoal = typeof legacyInput.goal === 'string' ? legacyInput.goal : '';
              title = summarizeLegacyPersona(legacyPersona);
              subtitle = 'Legacy persona';
              meta = '';
              goal = legacyGoal || legacyPersona;
            }

            const isDeletingThis = deleteMutation.isPending && pendingDelete?.id === item.id;
            return (
              <div key={item.id} className="group relative">
                <button
                  onClick={() => item.id && onSelectItem(item.id)}
                  className="w-full px-5 py-4 text-left transition hover:bg-muted/40"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                      <UserRound className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <div className="truncate text-sm font-medium text-foreground">{title}</div>
                        {structured && input.age_range && (
                          <span className="rounded-md border border-border bg-background px-1.5 py-0.5 text-[11px] text-muted-foreground">
                            {input.age_range}
                          </span>
                        )}
                      </div>
                      {subtitle && <div className="truncate text-xs text-muted-foreground">{subtitle}</div>}
                      {meta && (
                        <div className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                          <Building2 className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{meta}</span>
                        </div>
                      )}
                      {goal && (
                        <div className="flex gap-1.5 text-xs leading-5 text-muted-foreground">
                          <Target className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span className="line-clamp-2">{goal}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2 pr-12">
                      {item.status && (
                        <span className="inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400 capitalize">
                          {item.status.toLowerCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${title} from ${datasetName}`}
                  className={cn(
                    'absolute right-3 top-3 h-7 w-7 text-muted-foreground hover:text-destructive',
                    'opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100',
                    isDeletingThis && 'opacity-100',
                  )}
                  disabled={isDeletingThis || !item.id || !orgId}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPendingDelete(item);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this persona?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes them from "{datasetName}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!pendingDelete?.id || !orgId) return;
                const itemId = pendingDelete.id;
                deleteMutation.mutate(
                  { datasetName, itemId, organizationId: orgId },
                  {
                    onSuccess: () => {
                      toast({ title: 'Removed', description: 'Persona removed from population.' });
                      setPendingDelete(null);
                    },
                    onError: (err) => {
                      toast({
                        title: 'Error',
                        description: err instanceof Error ? err.message : 'Failed to remove persona.',
                        variant: 'destructive',
                      });
                    },
                  },
                );
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function summarizeLegacyPersona(value: string) {
  if (!value.trim()) return 'Unnamed persona';
  const roleMatch = value.match(/^(.+?)\s+at\s+/i);
  if (roleMatch?.[1] && roleMatch[1].length <= 80) return roleMatch[1];
  const firstClause = value.split(',')[0]?.trim();
  if (firstClause && firstClause.length <= 80) return firstClause;
  return value.length > 80 ? `${value.slice(0, 77)}...` : value;
}
